import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Star } from "lucide-react";
import { CONTACT, waLink } from "@/data";

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HEADLINE = [
  { text: "Reconnect", accent: false },
  { text: "with Your", accent: false },
  { text: "Inner Wisdom", accent: true },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const emblemY = useTransform(scrollYProgress, [0, 1], ["0%", "26%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "34%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden noir grain">
      <div className="gold-glow" />
      {/* soft focused glow anchored behind the headline (keeps left text area readable) */}
      <div className="absolute -left-40 top-1/3 w-[520px] h-[520px] rounded-full blur-[120px] bg-[radial-gradient(circle,rgba(124,58,237,0.20),transparent_65%)] pointer-events-none" />

      <motion.div style={{ opacity: fade }} className="section-pad relative z-10 min-h-screen grid lg:grid-cols-12 gap-10 items-center pt-28 pb-20">
        {/* Text */}
        <motion.div style={{ y: textY }} className="lg:col-span-7 flex flex-col">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[var(--gold-bright)]" />
              <span className="overline">Akashic Guidance &amp; Energy Healing</span>
            </div>
          </motion.div>

          <h1 data-testid="hero-headline" className="font-serif-display text-[var(--cream)] leading-[0.92] tracking-tight text-5xl sm:text-6xl lg:text-7xl xl:text-[6.6rem]">
            {HEADLINE.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  custom={i}
                  variants={lineReveal}
                  initial="hidden"
                  animate="show"
                  className={`block ${line.accent ? "text-[var(--gold-bright)] text-glow italic" : ""}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            className="font-sans-ui text-[var(--cream)]/75 text-base md:text-lg max-w-xl mt-8 leading-relaxed font-light"
          >
            Gain insight into recurring life patterns, receive compassionate spiritual guidance, and explore practices designed to support healing, clarity and conscious decision-making.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-10"
          >
            <a
              href={waLink("Namaste! I'd like to book a session with Brahmsara.")}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-book-btn"
              className="group font-sans-ui text-sm tracking-wide px-9 py-4 bg-[var(--gold)] text-[var(--noir)] rounded-full hover:bg-[var(--gold-bright)] transition-colors duration-400 shadow-[0_0_34px_rgba(233,172,70,0.28)]"
            >
              Book Your Session
            </a>
            <a
              href="#meditations"
              data-testid="hero-meditations-btn"
              className="font-sans-ui text-sm tracking-wide px-9 py-4 border border-[var(--cream)]/40 text-[var(--cream)] rounded-full hover:border-[var(--gold-bright)] hover:text-[var(--gold-bright)] transition-colors duration-400"
            >
              Explore Guided Meditations
            </a>
          </motion.div>

          {/* trust row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.45 }}
            className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-10"
          >
            <span className="flex items-center gap-2">
              <span className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} size={14} className="fill-[var(--gold)] text-[var(--gold)]" />
                ))}
              </span>
              <span className="font-sans-ui text-xs text-[var(--cream)]/70 tracking-wide">5.0 on Google</span>
            </span>
            <span className="h-4 w-px bg-[var(--cream)]/20" />
            <span className="font-sans-ui text-xs text-[var(--cream)]/70 tracking-wide">Sessions online, worldwide</span>
          </motion.div>
        </motion.div>

        {/* Emblem */}
        <div className="lg:col-span-5 hidden md:flex items-center justify-center">
          <motion.div
            style={{ y: emblemY }}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-[clamp(280px,30vw,440px)] aspect-square"
          >
            <div className="absolute inset-0 rounded-full blur-[70px] bg-[radial-gradient(circle,rgba(233,172,70,0.38),transparent_62%)]" />
            <motion.div
              className="absolute -inset-[6%] rounded-full border border-[rgba(233,172,70,0.22)]"
              animate={{ rotate: 360 }}
              transition={{ duration: 70, repeat: Infinity, ease: "linear" }}
            />
            <motion.div
              className="absolute inset-[3%] rounded-full border border-dashed border-[rgba(233,172,70,0.18)]"
              animate={{ rotate: -360 }}
              transition={{ duration: 110, repeat: Infinity, ease: "linear" }}
            />
            <motion.img
              src={CONTACT.logo}
              alt="Brahmsara emblem"
              className="absolute inset-[8%] rounded-full object-cover shadow-[0_0_90px_rgba(233,172,70,0.4)] ring-1 ring-[rgba(233,172,70,0.35)]"
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>
        </div>
      </motion.div>

      {/* fade into next section */}
      <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-b from-transparent to-[var(--noir)] pointer-events-none" />

      <motion.a
        href="#about"
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[var(--cream)]/70"
        data-testid="hero-scroll-cue"
      >
        <span className="overline text-[0.55rem]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} className="text-[var(--gold-bright)]" />
        </motion.span>
      </motion.a>
    </section>
  );
}
